/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Sortable admin tables on TanStack Table, kept headless so each page keeps its own
 * daisyUI <table> markup. Columns are sortable unless they set enableSorting: false;
 * values that are undefined sort last either way, so accessors return undefined for
 * "nothing to show".
 */

import React, { useState } from 'react'

import {
  type ColumnDef,
  type Header,
  type Row,
  type SortingState,
  type Table,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { LuChevronDown, LuChevronUp, LuChevronsUpDown } from 'react-icons/lu'

/** Per-column presentation; columnDef.meta is untyped in TanStack, so it is cast here. */
export interface AdminColumnMeta<T> {
  /** Classes for the column's <th>. */
  thClassName?: string
  /** Classes for each <td>. */
  className?: string
  /** Extra <td> attributes for one row, e.g. data-testid or title. */
  tdProps?: (row: T) => React.TdHTMLAttributes<HTMLTableCellElement> & { 'data-testid'?: string }
}

const metaOf = <T,>(meta: unknown) => (meta ?? {}) as AdminColumnMeta<T>

interface UseAdminTableOptions<T> {
  initialSorting?: SortingState
  /** Called when the admin changes the sort, e.g. to go back to page 1. */
  onSortingChange?: () => void
  /** Stable row ids, e.g. for React keys; defaults to the row's index. */
  getRowId?: (row: T) => string
}

// TanStack needs the value type per column; the columns of one table mix them.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AdminColumns<T> = ColumnDef<T, any>[]

export const useAdminTable = <T,>(
  data: T[],
  columns: AdminColumns<T>,
  { initialSorting = [], onSortingChange, getRowId }: UseAdminTableOptions<T> = {}
): Table<T> => {
  const [sorting, setSorting] = useState<SortingState>(initialSorting)
  return useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      setSorting(updater)
      onSortingChange?.()
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId,
    defaultColumn: { sortUndefined: 'last' },
    // Click order is ascending, descending, then back to the list's own order.
    sortDescFirst: false,
  })
}

const SortIcon: React.FC<{ sorted: false | 'asc' | 'desc' }> = ({ sorted }) => {
  if (sorted === 'asc') return <LuChevronUp className="text-sm" aria-hidden />
  if (sorted === 'desc') return <LuChevronDown className="text-sm" aria-hidden />
  // Hidden until hovered, as on the asset and tracker tables; the active sort stays visible.
  return <LuChevronsUpDown className="text-sm opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
}

export const AdminTh = <T,>({ header }: { header: Header<T, unknown> }) => {
  const { thClassName } = metaOf<T>(header.column.columnDef.meta)
  const content = header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())
  if (!header.column.getCanSort()) return <th className={thClassName}>{content}</th>
  const sorted = header.column.getIsSorted()
  return (
    <th className={thClassName} aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none'}>
      <button
        type="button"
        className="group inline-flex cursor-pointer select-none items-center gap-0.5 whitespace-nowrap"
        onClick={header.column.getToggleSortingHandler()}
      >
        {content}
        <SortIcon sorted={sorted} />
      </button>
    </th>
  )
}

/** The header cells, for a <tr> that may put its own cell (e.g. select-all) first. */
export const AdminHeaderCells = <T,>({ table }: { table: Table<T> }) => (
  <>
    {table.getHeaderGroups()[0].headers.map((header) => (
      <AdminTh key={header.id} header={header} />
    ))}
  </>
)

export const AdminRowCells = <T,>({ row }: { row: Row<T> }) => (
  <>
    {row.getVisibleCells().map((cell) => {
      const { className, tdProps } = metaOf<T>(cell.column.columnDef.meta)
      return (
        <td key={cell.id} className={className} {...tdProps?.(row.original)}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      )
    })}
  </>
)
